class ProductGwp extends HTMLElement {
  constructor() {
    super();
    this.gwpEl = this.querySelector('[data-gwp]');
    this.gwpAtcButton = this.querySelector('[data-gwp-atc-button]');
    this.gwpAtcButtonText = this.gwpAtcButton?.querySelector('[data-gwp-atc-text]');
    this.gwpAtcButtonSpinner = this.gwpAtcButton?.querySelector('[data-gwp-atc-spinner]');
    this.error = this.querySelector('[data-gwp-error]');
    this.errorMessage = this.querySelector('[data-gwp-error-message]');
    this.errorNoStock = this.querySelector('[data-gwp-error-no-stock]');
    this.variantImage = this.querySelector('[data-gwp-variant-image]');
    this.dismissButton = this.querySelector('[data-gwp-dismiss]');

    // Add image caching properties
    this.variantImagesCache = new Map();
    this.imagePreloadQueue = [];
    this._variantChangeDepth = 0;

    this.gwpAtcButton?.addEventListener('click', () => this.handleAtc(this));
    this.dismissButton?.addEventListener('click', () => this.handleDismiss());

    document.addEventListener('rebuy:cart.change', (event) => {
      const cart = event.detail.cart.cart;
      if (this.error) this.error.classList.add('hidden');
      this.updateGwp(cart);
    });

    // Listen for clicks on dropdown options
    this.gwpEl.querySelectorAll('[data-dropdown-option]').forEach((option) => {
      option.addEventListener('click', () => this.handleVariantChange('dropdown'));
    });

    // Add new event listeners for swatch radios
    this.gwpEl.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.addEventListener('change', () => this.handleVariantChange('radio'));
    });

    // Add initial hidden state to prevent flash
    const elementsToHide = [
      this.querySelector('[data-gwp-description-and-bar]'),
      this.querySelector('[data-gwp-text-empty]'),
      this.querySelector('[data-gwp-text-progress]'),
      this.querySelector('[data-gwp-text-qualifies]'),
      this.querySelector('[data-gwp-text-success]'),
      this.querySelector('[data-gwp-variant-selectors]'),
    ];

    elementsToHide.forEach((element) => {
      if (element) element.classList.add('hidden');
    });

    // Immediately check initial state
    this.updateGwp();

    // Start prefetching variant images
    this.prefetchVariantImages();

    // After setting up event listeners in the constructor:
    this.handleVariantChange();

    // Initialize dropdown prices
    this.initializeDropdownPrices();

    // Initialize GWP validation
    this.validateGWP();
  }

  getGwpBarProgress(cartSubtotal, thresholdValueCents) {
    return Math.min((cartSubtotal / thresholdValueCents) * 100, 100);
  }

  updateGwpBarProgress(progress) {
    const gwpBar = this.gwpEl.querySelector('[data-gwp-line]');

    gwpBar.style.width = `${progress}%`;
    gwpBar.classList.toggle(
      gwpBar.classList.contains('after:bg-brand-02') ? 'after:bg-brand-02' : 'after:bg-darkest',
      progress > 0,
    );
    gwpBar.classList.toggle('after:bg-[#b1b1b1]', progress === 0);
    gwpBar.classList.toggle('after:mr-[9px]', progress > 50);
    gwpBar.classList.toggle('after:mr-0', progress <= 50);
  }

  updateGwpElements(progress, cartSubtotal, thresholdValueCents, isGiftInCart) {
    const gwpDescriptionAndBar = this.gwpEl.querySelector('[data-gwp-description-and-bar]');
    const gwpTextEmpty = this.gwpEl.querySelector('[data-gwp-text-empty]');
    const gwpTextProgress = this.gwpEl.querySelector('[data-gwp-text-progress]');
    const gwpTextQualifies = this.gwpEl.querySelector('[data-gwp-text-qualifies]');
    const gwpTextSuccess = this.gwpEl.querySelector('[data-gwp-text-success]');
    const progressPrice = gwpTextProgress.querySelector('[data-gwp-text-price]');
    const variantSelectors = this.gwpEl.querySelector('[data-gwp-variant-selectors]');
    const gwpHeading = this.gwpEl.querySelectorAll('[data-gwp-heading]');
    const gwpHeadingFull = this.gwpEl.querySelectorAll('[data-gwp-heading-full]');
    const gwpSubheading = this.gwpEl.querySelectorAll('[data-gwp-subheading]');
    const gwpSubheadingFull = this.gwpEl.querySelectorAll('[data-gwp-subheading-full]');
    const gwpSuccessHeading = this.gwpEl.querySelectorAll('[data-gwp-success-heading]');

    // Update progress price
    if (progressPrice) {
      progressPrice.textContent = ((thresholdValueCents - cartSubtotal) / 100).toFixed(2);
    }

    // Determine states
    const isState3 = progress === 100 && !isGiftInCart;
    const isState4 = progress === 100 && isGiftInCart;

    // Set display of text strings - use lg:hidden for desktop override
    if (gwpTextEmpty) {
      gwpTextEmpty.classList.toggle('hidden', progress > 0);
      gwpTextEmpty.classList.toggle('lg:hidden', progress > 0);
    }
    if (gwpTextProgress) {
      gwpTextProgress.classList.toggle('hidden', progress === 0 || progress === 100);
      gwpTextProgress.classList.toggle('lg:hidden', progress === 0 || progress === 100);
    }
    // Keep description-and-bar visible in State 4 so success heading can show
    if (gwpDescriptionAndBar) {
      gwpDescriptionAndBar.classList.toggle('hidden', false);
      gwpDescriptionAndBar.classList.toggle('lg:hidden', false);
    }
    if (gwpTextQualifies) {
      gwpTextQualifies.classList.toggle('hidden', !isState3);
      gwpTextQualifies.classList.toggle('lg:hidden', !isState3);
    }
    if (gwpTextSuccess) {
      gwpTextSuccess.classList.toggle('hidden', !isState4);
      gwpTextSuccess.classList.toggle('lg:hidden', !isState4);
    }
    if (variantSelectors) variantSelectors.classList.toggle('hidden', isGiftInCart);

    // Update headings based on state - use lg:hidden for desktop override
    if (gwpHeading.length) {
      gwpHeading.forEach((heading) => {
        heading.classList.toggle('hidden', isState3 || isState4);
        heading.classList.toggle('lg:hidden', isState3 || isState4);
      });
    }
    if (gwpHeadingFull.length) {
      gwpHeadingFull.forEach((heading) => {
        heading.classList.toggle('hidden', !isState3);
        heading.classList.toggle('lg:hidden', !isState3);
      });
    }
    // Update subheadings based on state - regular subheading shows in states 1 & 2 only
    if (gwpSubheading.length) {
      gwpSubheading.forEach((subheading) => {
        subheading.classList.toggle('hidden', isState3 || isState4);
        subheading.classList.toggle('lg:hidden', isState3 || isState4);
      });
    }
    if (gwpSubheadingFull.length) {
      gwpSubheadingFull.forEach((subheading) => {
        subheading.classList.toggle('hidden', !isState3);
        subheading.classList.toggle('lg:hidden', !isState3);
      });
    }
    if (gwpSuccessHeading.length) {
      gwpSuccessHeading.forEach((heading) => {
        heading.classList.toggle('hidden', !isState4);
        heading.classList.toggle('lg:hidden', !isState4);
      });
    }

    // Hide progress bar in State 4
    const gwpProgressBar = this.gwpEl.querySelector('[data-gwp-progress-bar]');
    if (gwpProgressBar) {
      gwpProgressBar.classList.toggle('hidden', isState4);
      gwpProgressBar.classList.toggle('lg:hidden', isState4);
    }
  }

  checkForBuyItemInCart(cart) {
    const buyItemProductIds = this.gwpEl.dataset.gwpBuyProducts || false;
    if (buyItemProductIds) {
      const buyItemProductIdsArr = JSON.parse(buyItemProductIds);

      // Count total qualifying items in cart, including quantities
      const qualifyingItemsCount = cart.items.reduce((total, item) => {
        if (buyItemProductIdsArr.includes(parseInt(item.product_id, 10))) {
          return total + item.quantity;
        }
        return total;
      }, 0);

      return qualifyingItemsCount;
    } else {
      return 0;
    }
  }

  checkForGiftInCart(cart, widgetId) {
    // Count total GWP items in cart, including quantities
    const gwpCount = cart.items.reduce((total, item) => {
      // If widgetId exists, check for matching _widget_id property
      if (widgetId && !isNaN(widgetId)) {
        if (item.properties && parseInt(item.properties['_widget_id'], 10) === parseInt(widgetId, 10)) {
          return total + item.quantity;
        }
      } else {
        // If no widgetId, check for items with _tier: property (property key, not value)
        if (item.properties && item.properties.hasOwnProperty('_tier')) {
          return total + item.quantity;
        }
      }
      return total;
    }, 0);

    return gwpCount;
  }

  updateBuyxGetyElements(gwpCount, qualifyingItemsCount) {
    const gwpDescriptionAndBar = this.gwpEl.querySelector('[data-gwp-description-and-bar]');
    const gwpTextEmpty = this.gwpEl.querySelector('[data-gwp-text-empty]');
    const gwpTextSuccess = this.gwpEl.querySelector('[data-gwp-text-success]');
    const variantSelectors = this.gwpEl.querySelector('[data-gwp-variant-selectors]');

    // Determine states
    const isGiftInCart = gwpCount > 0;
    const isBuyItemInCart = qualifyingItemsCount > 0;
    const showSuccessState = gwpCount === qualifyingItemsCount && qualifyingItemsCount > 0;

    // Set display of text strings
    if (gwpDescriptionAndBar) gwpDescriptionAndBar.classList.toggle('hidden', showSuccessState);
    if (gwpTextEmpty) gwpTextEmpty.classList.toggle('hidden', showSuccessState);
    if (variantSelectors) variantSelectors.classList.toggle('hidden', showSuccessState);
    if (gwpTextSuccess) gwpTextSuccess.classList.toggle('hidden', !showSuccessState);
  }

  updateGwpAtc(type, progress, gwpCount, qualifyingItemsCount, isDismissed = false) {
    if (type === 'gwp') {
      if (this.gwpAtcButton) {
        this.gwpAtcButton.classList.toggle('hidden', !(progress === 100 && !gwpCount));
      }
    } else if (type === 'buyxgety') {
      const showSuccessState = gwpCount === qualifyingItemsCount && qualifyingItemsCount > 0;
      const canAddMore = isDismissed || !showSuccessState;

      if (this.gwpAtcButton) {
        this.gwpAtcButton.setAttribute('data-gwp-qualified', qualifyingItemsCount >= 1);
        this.gwpAtcButton.classList.toggle('hidden', !canAddMore);
      }
    }
  }

  handleAtc() {
    const gwpData = JSON.parse(this.gwpEl.dataset.gwp);
    const widgetId = JSON.parse(this.gwpEl.dataset.gwpWidgetId);
    const isBuyxGety = !!this.gwpEl.dataset.gwpBuyProducts;

    if (this.error) {
      this.error.classList.add('hidden');
      this.errorMessage.classList.remove('hidden');
      this.errorNoStock.classList.add('hidden');
    }
    if (this.gwpAtcButton) this.gwpAtcButton.disabled = true;
    if (this.gwpAtcButtonText) this.gwpAtcButtonText.classList.add('hidden');
    if (this.gwpAtcButtonSpinner) this.gwpAtcButtonSpinner.classList.remove('hidden');

    const selectedVariant = {};
    this.gwpEl.querySelectorAll('[data-dropdown-menu]').forEach((dropdown) => {
      const selected = dropdown.querySelector('[data-dropdown-option="selected"]');
      if (!selected?.dataset?.dropdownOptionValue) return;
      const optionIndex = dropdown.dataset.optionIndex;
      if (!optionIndex) return;
      selectedVariant[`option${optionIndex}`] = selected.dataset.dropdownOptionValue;
    });
    const selectedVariantObject = this.selectedVariantObject;

    if (isBuyxGety) {
      const isQualified = this.gwpAtcButton.getAttribute('data-gwp-qualified') === 'true';
      this.resetButton();

      if (!isQualified) {
        this.error.classList.remove('hidden');
        return;
      }
    }

    if (selectedVariantObject && selectedVariantObject.available) {
      window.Rebuy.Cart.addItem({
        id: selectedVariantObject.id,
        quantity: 1,
        properties: {
          _widget_id: widgetId,
          _attribution: 'Rebuy Product',
          _source: 'Rebuy',
        },
      });
    } else {
      this.error.classList.remove('hidden');
      this.errorMessage.classList.add('hidden');
      this.errorNoStock.classList.remove('hidden');
    }
  }

  resetButton() {
    if (this.gwpAtcButton) this.gwpAtcButton.disabled = false;
    if (this.gwpAtcButtonText) this.gwpAtcButtonText.classList.remove('hidden');
    if (this.gwpAtcButtonSpinner) this.gwpAtcButtonSpinner.classList.add('hidden');
  }

  async updateGwp(cartObject, isDismissed = false) {
    const widgetIdValue = this.gwpEl.dataset.gwpWidgetId;
    const widgetId = widgetIdValue && widgetIdValue !== '' ? parseInt(widgetIdValue) : null;

    // If a cart object wasn't passed through with the function call, get the cart via AJAX.
    let cart = cartObject;
    if (!cart) {
      const response = await fetch(`${routes.cart_url}.js`);
      cart = await response.json();
    }

    // If dismissed, treat as if there are no qualifying items
    const gwpCount = isDismissed ? 0 : this.checkForGiftInCart(cart, widgetId);
    const qualifyingItemsCount = isDismissed ? 0 : this.checkForBuyItemInCart(cart);

    if (this.gwpEl.dataset.gwpThresholdValue) {
      const thresholdValue = parseInt(this.gwpEl.dataset.gwpThresholdValue);
      const thresholdValueCents = thresholdValue * 100;
      const progress = isDismissed ? 0 : this.getGwpBarProgress(cart.total_price, thresholdValueCents);

      const isGiftInCart = gwpCount > 0;

      this.updateGwpBarProgress(progress);
      this.updateGwpElements(progress, cart.total_price, thresholdValueCents, isGiftInCart);
      this.updateGwpAtc('gwp', progress, gwpCount, undefined, isDismissed);
    } else if (this.gwpEl.dataset.gwpBuyProducts) {
      this.updateBuyxGetyElements(gwpCount, qualifyingItemsCount);
      this.updateGwpAtc('buyxgety', undefined, gwpCount, qualifyingItemsCount, isDismissed);
    }

    // Reset button state
    this.resetButton();

    // Remove initial hidden state after first update
    this.gwpEl.classList.remove('opacity-0');
  }

  handleVariantChange() {
    if (this._variantChangeDepth > 12) return;
    this._variantChangeDepth += 1;
    try {
      this.runVariantChange();
    } finally {
      this._variantChangeDepth -= 1;
    }
  }

  runVariantChange() {
    const gwpData = JSON.parse(this.gwpEl.dataset.gwp);
    const selectedVariant = {};

    // Collect dropdown selections
    this.gwpEl.querySelectorAll('[data-dropdown-menu]').forEach((dropdown) => {
      const selected = dropdown.querySelector('[data-dropdown-option="selected"]');
      if (selected?.dataset?.dropdownOptionValue && dropdown.dataset.optionIndex) {
        selectedVariant[`option${dropdown.dataset.optionIndex}`] = selected.dataset.dropdownOptionValue;
      }
    });

    // Collect radio selections
    this.gwpEl.querySelectorAll('input[type="radio"]:checked').forEach((radio) => {
      const optionNumber = this.optionIndexFromRadioName(radio.name);
      if (optionNumber) {
        selectedVariant[`option${optionNumber}`] = radio.value;
      }
    });

    // Update available options in dropdowns
    this.updateAvailableOptions(gwpData, selectedVariant);
    this.syncOption3Visibility(gwpData, selectedVariant);
    this.updateAvailableOptions(gwpData, selectedVariant);

    this.selectedVariantObject = gwpData.variants.find(
      (variant) =>
        variant.option1 === selectedVariant.option1 &&
        (selectedVariant.option2 ? variant.option2 === selectedVariant.option2 : true) &&
        (selectedVariant.option3 ? variant.option3 === selectedVariant.option3 : true),
    );

    // Update variant image if it exists
    if (this.variantImage && this.selectedVariantObject) {
      // Check if we have a cached image
      const cachedImage = this.variantImagesCache.get(this.selectedVariantObject.id);

      if (cachedImage) {
        // Use cached image
        this.variantImage.src = cachedImage.src;
        this.variantImage.alt = this.selectedVariantObject.featured_image.alt || gwpData.title;
      } else {
        // Fallback to regular loading if cache miss
        const variantImage = gwpData.images[this.selectedVariantObject.featured_image.position - 1];
        if (variantImage) {
          const currentWidth = this.variantImage.width * 2; // Multiply by 2 since we're displaying at half size
          const newImageUrl = variantImage.replace(/(\.[^.]*)$/, `_${currentWidth}x$1`);
          this.variantImage.src = newImageUrl;
          this.variantImage.alt = this.selectedVariantObject.featured_image.alt || gwpData.title;
        }
      }
    }

    // Update strikethrough price if it exists
    const strikethroughPrice = this.gwpEl.querySelector('[data-gwp-strikethrough-price]');
    const promoPrice = this.gwpEl.querySelector('[data-gwp-promo-price]');
    if (strikethroughPrice && this.selectedVariantObject) {
      const formattedPrice = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(this.selectedVariantObject.price / 100);
      strikethroughPrice.textContent = formattedPrice;
    }

    if (promoPrice && this.selectedVariantObject) {
      const promoPriceType = promoPrice.getAttribute('data-promo-type');
      const promoPricePercent = parseFloat(promoPrice.dataset.promoPercent);
      const promoPriceAmount = parseFloat(promoPrice.dataset.promoAmount);
      if (promoPriceType === 'percent') {
        const formattedPrice = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(
          (this.selectedVariantObject.price - (this.selectedVariantObject.price * promoPricePercent) / 100) / 100,
        );
        promoPrice.textContent = formattedPrice;
      } else if (promoPriceType === 'amount') {
        const formattedPrice = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format((this.selectedVariantObject.price - promoPriceAmount) / 100);
        promoPrice.textContent = formattedPrice;
      }
    }

    // Update button state based on variant availability
    if (this.selectedVariantObject && this.selectedVariantObject.available) {
      if (this.error) this.error.classList.add('hidden');
      if (this.gwpAtcButton) this.gwpAtcButton.disabled = false;
    } else {
      if (this.error) {
        this.error.classList.remove('hidden');
        this.errorMessage.classList.add('hidden');
        this.errorNoStock.classList.remove('hidden');
      }
      if (this.gwpAtcButton) this.gwpAtcButton.disabled = true;
    }

    this.updateSetDescription();

    // Update dropdown prices
    this.initializeDropdownPrices();

    // Update option 1 value
    const option1Value = this.gwpEl.querySelector('[data-gwp-option-1-value]');
    if (option1Value && this.selectedVariantObject) {
      option1Value.textContent = this.selectedVariantObject.option1;
    }
  }

  formatSetDescription(value) {
    return String(value)
      .replace(/,\s*/g, '\n') // Put newlines after commas
      .replace(/(:)(?!\n)/g, '$1\n'); // Put newlines after colons
  }

  updateSetDescription() {
    const setDescriptionEl = this.gwpEl.querySelector('[data-gwp-set-description]');
    if (!setDescriptionEl) return;

    const variant = this.selectedVariantObject;
    if (!variant?.available) {
      setDescriptionEl.classList.add('hidden');
      setDescriptionEl.textContent = '';
      return;
    }

    const metafieldsScript = this.querySelector('script[data-gwp-metafields-json]');
    if (!metafieldsScript) return;

    let metafields;
    try {
      metafields = JSON.parse(metafieldsScript.textContent);
    } catch {
      setDescriptionEl.classList.add('hidden');
      setDescriptionEl.textContent = '';
      return;
    }

    const variantId = String(variant.id);
    const raw =
      metafields?.variants?.[variantId]?.metafields?.custom?.set_description?.value ??
      metafields?.variants?.[variant.id]?.metafields?.custom?.set_description?.value;

    if (!raw) {
      setDescriptionEl.classList.add('hidden');
      setDescriptionEl.textContent = '';
      return;
    }

    setDescriptionEl.textContent = this.formatSetDescription(raw);
    setDescriptionEl.classList.remove('hidden');
  }

  /**
   * Selections for cascade checks at option level `throughIndex` (1..3):
   * options 1..throughIndex-1 from current DOM state, option `throughIndex` = valueAtThrough.
   * Deeper options are omitted so e.g. option3 does not block choosing Twin on option2.
   */
  selectionsThrough(currentSelections, throughIndex, valueAtThrough) {
    const selectionsThroughObj = {};
    for (let i = 1; i < throughIndex; i++) {
      const optionKey = `option${i}`;
      const optionValue = currentSelections[optionKey];
      const optionValueIsDefinedAndNonEmpty = optionValue != null && optionValue !== '';
      if (optionValueIsDefinedAndNonEmpty) {
        selectionsThroughObj[optionKey] = optionValue;
      }
    }
    selectionsThroughObj[`option${throughIndex}`] = valueAtThrough;
    return selectionsThroughObj;
  }

  /**
   * Variant availability check: prior options + candidate value + any later picks
   * (e.g. selected size when testing whether a color swatch is in stock).
   */
  availabilityTestSelections(currentSelections, optionIndex, candidateValue) {
    const test = this.selectionsThrough(currentSelections, optionIndex, candidateValue);
    for (let i = optionIndex + 1; i <= 3; i++) {
      const key = `option${i}`;
      const value = currentSelections[key];
      if (value != null && value !== '') {
        test[key] = value;
      }
    }
    return test;
  }

  /** Option index from radio name (e.g. "option1", "option1-a"). */
  optionIndexFromRadioName(name) {
    return name.match(/option(\d+)/)?.[1] ?? null;
  }

  /** All variant radios for one option inside this widget. */
  getOptionRadios(optionIndex) {
    const index = String(optionIndex);
    return [...this.gwpEl.querySelectorAll('input[type="radio"]')].filter(
      (radio) => this.optionIndexFromRadioName(radio.name) === index,
    );
  }

  getSwatchForRadio(radio) {
    return this.gwpEl.querySelector(`label[for="${radio.id}"] .swatch`);
  }

  /** Swatches stay selectable with a crossout; button pickers use disabled + data-disabled. */
  setOptionRadioAvailability(radio, isAvailable) {
    const swatch = this.getSwatchForRadio(radio);
    if (swatch) {
      radio.disabled = false;
      radio.removeAttribute('disabled');
      swatch.classList.toggle('unavailable', !isAvailable);
      return;
    }

    radio.disabled = !isAvailable;
    if (isAvailable) {
      radio.removeAttribute('data-disabled');
    } else {
      radio.setAttribute('data-disabled', 'true');
    }
    const label = this.gwpEl.querySelector(`label[for="${radio.id}"]`);
    label?.classList.toggle('unavailable', !isAvailable);
    radio.parentElement?.classList.toggle('unavailable', !isAvailable);
  }

  initializeDropdownPrices() {
    // Update prices for all dropdown menus
    this.gwpEl.querySelectorAll('[data-dropdown-menu]').forEach((dropdown) => {
      const selectedOption = dropdown.querySelector('[data-dropdown-option="selected"]');
      if (selectedOption) {
        const optionPrice = selectedOption.dataset.dropdownOptionPrice;
        const dropdownMenu = dropdown.closest('custom-dropdown-menu');

        if (dropdownMenu && optionPrice) {
          const dropdownStrikethroughPrice = dropdownMenu.querySelector('[data-dropdown-strikethrough-price]');

          if (dropdownStrikethroughPrice) {
            const formattedPrice = new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(optionPrice / 100);

            dropdownStrikethroughPrice.textContent = formattedPrice;
          }
        }
      }
    });
  }

  // Helper to check if a variant matches the current selections
  isVariantMatch(variant, testSelections) {
    return [1, 2, 3].every((idx) => {
      const key = `option${idx}`;
      if (variant[key] !== undefined && testSelections[key] !== undefined) {
        return variant[key] === testSelections[key];
      }
      return true;
    });
  }

  /** Variants matching option1 + option2 only; used to infer valid option3 values for the current choice. */
  getValidOption3Values(availableVariants, selections) {
    const matching = availableVariants.filter((v) => {
      if (v.option1 !== selections.option1) return false;
      if (selections.option2 && v.option2 !== selections.option2) return false;
      return true;
    });
    const values = new Set();
    matching.forEach((v) => {
      if (v.option3) values.add(v.option3);
    });
    return [...values];
  }

  setOption3SelectedInDom(option3Menu, value) {
    if (!option3Menu || value == null) return;
    option3Menu.querySelectorAll('[data-dropdown-option]').forEach((opt) => {
      const matches = opt.dataset.dropdownOptionValue === value;
      opt.setAttribute('data-dropdown-option', matches ? 'selected' : '');
    });
    const textEl = option3Menu.closest('custom-dropdown-menu')?.querySelector('[data-dropdown-text]');
    if (textEl) textEl.textContent = value;
  }

  /**
   * When only one option3 is valid for the current option1/2, auto-select it and hide the visible
   * third dropdown. When multiple are valid again, show the dropdown.
   */
  syncOption3Visibility(gwpData, selections) {
    if (!this.gwpEl.dataset.gwpSyncOption3Visibility) return;
    const option3Menu = this.gwpEl.querySelector('[data-dropdown-menu][data-option-index="3"]');
    if (!option3Menu) return;

    const valid = this.getValidOption3Values(gwpData.variants, selections);
    const wrapper = option3Menu.closest('custom-dropdown-menu');

    if (valid.length === 0) {
      wrapper?.classList.remove('hidden');
      return;
    }

    if (valid.length === 1) {
      const only = valid[0];
      this.setOption3SelectedInDom(option3Menu, only);
      selections.option3 = only;
      wrapper?.classList.add('hidden');
    } else {
      wrapper?.classList.remove('hidden');
    }
  }

  updateAvailableOptions(gwpData, currentSelections) {
    const availableVariants = gwpData.variants;

    // --- Handle dropdowns by data-option-index (1 → 2 → 3 so later options use corrected earlier picks) ---
    const dropdowns = Array.from(this.gwpEl.querySelectorAll('[data-dropdown-menu][data-option-index]')).sort(
      (a, b) => parseInt(a.dataset.optionIndex, 10) - parseInt(b.dataset.optionIndex, 10),
    );
    dropdowns.forEach((dropdown) => {
      const optionIndex = parseInt(dropdown.dataset.optionIndex, 10);
      const options = dropdown.querySelectorAll('[data-dropdown-option]');
      options.forEach((option) => {
        const value = option.dataset.dropdownOptionValue;
        const testSelections = this.availabilityTestSelections(currentSelections, optionIndex, value);
        const isValid = availableVariants.some((variant) => this.isVariantMatch(variant, testSelections));
        if (!isValid) {
          option.classList.add('opacity-50', 'line-through', 'pointer-events-none', 'cursor-not-allowed');
        } else {
          option.classList.remove('opacity-50', 'line-through', 'pointer-events-none', 'cursor-not-allowed');
        }
      });
      // If current selection is invalid, find first valid option
      const selectedOption = dropdown.querySelector('[data-dropdown-option="selected"]');
      if (selectedOption && selectedOption.dataset.dropdownOptionValue) {
        const selectedValue = selectedOption.dataset.dropdownOptionValue;
        const isStillValid = availableVariants.some((variant) => {
          const testSelections = this.availabilityTestSelections(currentSelections, optionIndex, selectedValue);
          return this.isVariantMatch(variant, testSelections);
        });
        if (!isStillValid) {
          // Find first valid option
          const firstValidOption = Array.from(options).find((opt) => {
            const value = opt.dataset.dropdownOptionValue;
            const testSelections = this.availabilityTestSelections(currentSelections, optionIndex, value);
            return availableVariants.some((variant) => this.isVariantMatch(variant, testSelections));
          });
          if (firstValidOption) {
            selectedOption.setAttribute('data-dropdown-option', '');
            firstValidOption.setAttribute('data-dropdown-option', 'selected');
            // Update button text
            const button = dropdown.closest('custom-dropdown-menu')?.querySelector('[data-dropdown-text]');
            if (button) {
              button.textContent = firstValidOption.dataset.dropdownOptionValue;
            }
            // Update currentSelections
            currentSelections[`option${optionIndex}`] = firstValidOption.dataset.dropdownOptionValue;
          }
        }
      }
    });

    // --- Swatch radio input availability logic ---
    for (let optionIdx = 1; optionIdx <= 3; optionIdx++) {
      const radios = this.getOptionRadios(optionIdx);
      if (!radios.length) continue;

      // First, reset all radios and their labels/containers to visible
      radios.forEach((radio) => {
        this.getSwatchForRadio(radio)?.classList.remove('unavailable');
        radio.disabled = false;
        radio.removeAttribute('disabled');
        const label = this.gwpEl.querySelector(`label[for="${radio.id}"]`);
        label?.classList.remove('unavailable', 'hidden');
        radio.parentElement?.classList.remove('unavailable', 'hidden');
      });

      // Now, apply availability logic
      radios.forEach((radio) => {
        const testSelections = this.availabilityTestSelections(currentSelections, optionIdx, radio.value);
        const isAvailable = availableVariants.some(
          (variant) => this.isVariantMatch(variant, testSelections) && variant.available,
        );
        this.setOptionRadioAvailability(radio, isAvailable);
      });

      // Auto-switch only for disabled button pickers (not swatches)
      const checked = radios.find((radio) => radio.checked);
      if (checked?.disabled && !this.getSwatchForRadio(checked)) {
        const firstAvailable = radios.find((radio) => !radio.disabled);
        if (firstAvailable) {
          firstAvailable.checked = true;
          this.handleVariantChange();
        }
      }
    }
  }

  handleDismiss() {
    // Reset any error states
    if (this.error) this.error.classList.add('hidden');

    // Reset the button state
    this.resetButton();

    // Force update the GWP widget to show initial state with isDismissed flag
    this.updateGwp(null, true);
  }

  prefetchVariantImages() {
    if (!this.gwpEl?.dataset.gwp) return;

    try {
      const gwpData = JSON.parse(this.gwpEl.dataset.gwp);

      // Queue all variant images for preloading
      gwpData.variants.forEach((variant) => {
        if (variant.featured_image) {
          const variantImage = gwpData.images[variant.featured_image.position - 1];
          if (variantImage) {
            const currentWidth = this.variantImage ? this.variantImage.width * 2 : 212; // Default to 212 if width not available
            const imageUrl = variantImage.replace(/(\.[^.]*)$/, `_${currentWidth}x$1`);
            this.queueImagePreload(imageUrl, variant.id);
          }
        }
      });

      // Start preloading process
      this.processImageQueue();
    } catch (e) {
      console.error('Error prefetching GWP variant images:', e);
    }
  }

  queueImagePreload(imageUrl, variantId) {
    this.imagePreloadQueue.push({ imageUrl, variantId });
  }

  processImageQueue() {
    if (!this.imagePreloadQueue.length) return;

    const { imageUrl, variantId } = this.imagePreloadQueue.shift();

    const img = new Image();
    img.onload = () => {
      this.variantImagesCache.set(variantId, img);
      // Process next image in queue
      this.processImageQueue();
    };
    img.onerror = () => {
      console.warn(`Failed to preload GWP variant image for variant ID ${variantId}: ${imageUrl}`);
      // Continue processing the queue even if this image failed
      this.processImageQueue();
    };
    img.src = imageUrl;
  }

  validateGWP() {
    // First check if there is a threshold to determine if cart validation is needed
    const thresholdValue = this.gwpEl.dataset.gwpThresholdValue;
    if (!thresholdValue) {
      return; // No threshold means no validation needed
    }

    const thresholdValueCents = parseInt(thresholdValue) * 100;
    const widgetId = parseInt(this.gwpEl.dataset.gwpWidgetId);

    // Check if validation listener is already set up
    if (this.gwpValidationListener) {
      return; // Already set up
    }

    // Create the validation handler
    this.gwpValidationHandler = async (event) => {
      const cart = event.detail.cart.cart;

      // Fetch the cart to get current state
      const response = await fetch(`${routes.cart_url}.js`);
      const currentCart = await response.json();

      // Check if any items with rebuy attributes are in the cart
      const rebuyItems = currentCart.items.filter(
        (item) =>
          item.properties &&
          item.properties._widget_id &&
          parseInt(item.properties._widget_id) === widgetId &&
          item.properties._attribution === 'Rebuy Product' &&
          item.properties._source === 'Rebuy',
      );

      if (rebuyItems.length > 0) {
        // Calculate cart total minus the price of GWP items (since they should be free)
        const gwpItemsTotal = rebuyItems.reduce((total, item) => {
          return total + (item.final_line_price || item.line_price);
        }, 0);

        const cartTotalMinusGwp = currentCart.total_price - gwpItemsTotal;
        const meetsThreshold = cartTotalMinusGwp >= thresholdValueCents;

        if (meetsThreshold) {
          // Threshold is met and rebuy items are present - validation successful
        } else {
          // Threshold not met but rebuy items are present - remove them
          await this.removeRebuyItemsFromCart(rebuyItems);
        }
      }
    };

    // Listen for cart changes
    document.addEventListener('rebuy:cart.change', this.gwpValidationHandler);
    this.gwpValidationListener = true;
  }

  async removeRebuyItemsFromCart(rebuyItems) {
    // Remove each rebuy item from the cart using the item key
    for (const item of rebuyItems) {
      try {
        // Use the item key for removal - this is more reliable than line numbers
        const itemKey = item.key;

        if (!itemKey) {
          console.error('Could not find item key for removal:', item);
          continue;
        }

        // Use Shopify's cart change API to remove the item by key
        const response = await fetch(`${routes.cart_change_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: itemKey,
            quantity: 0,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Failed to remove rebuy item from cart:', {
            item,
            itemKey,
            status: response.status,
            error: errorText,
          });
        } else {
          console.log(`Successfully removed item with key ${itemKey}`);
        }
      } catch (error) {
        console.error('Error removing rebuy item from cart:', error);
      }
    }
  }
}

if (customElements.get('product-gwp') === undefined) {
  customElements.define('product-gwp', ProductGwp);
}
